import { cn } from '@repo/tailwind-config/tailwind.functions'
import { tailwindColors } from '@repo/tailwind-config/tailwind.vars'
import { toFullName } from '@repo/toolkit/to-full-name'
import { SVGFC } from '@repo/toolkit/types'
import ActionButton from '@repo/ui/action-button'
import {
  AddUserIcon,
  ChildrenIcon,
  DeleteIcon,
  DocumentIcon,
  OpenEyeIcon,
  PenIcon,
  UploadIcon as UploadSVGIcon,
  UserTwoIcon
} from '@repo/ui/icons'
import Typography from '@repo/ui/typography'

import useTranslate from '../../../../lib/hooks/use-translate'
import { CenterGroup } from '../../../../lib/types/models/center-group/center-group'
import CenterGroupCircle from './center-group-circle'

type Props = {
  group: CenterGroup
  uploadIcon?: SVGFC
  onEdit?: () => void
  onDelete?: () => void
  onAssignTefl?: () => void
  onUploadDocument?: () => void
  onViewStudents?: () => void
}

const CenterGroupCard = ({
  group,
  uploadIcon: UploadIcon = UploadSVGIcon,
  onEdit,
  onDelete,
  onAssignTefl,
  onUploadDocument,
  onViewStudents
}: Props) => {
  const studentsCount = group.students?.length ?? 0
  const documentsCount = group.documents?.length ?? 0
  const hasTefl = !!group.tefl
  const { t } = useTranslate()

  return (
    <div className="w-full rounded-lg p-4 shadow">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <CenterGroupCircle color={group.color} />
          <Typography variant="text" className="text-primary">
            {group.name}
          </Typography>
        </div>

        <div className="flex items-center gap-2">
          {onDelete && (
            <ActionButton size="small" variant="danger" icon={DeleteIcon} onClick={onDelete} />
          )}
          {onEdit && <ActionButton size="small" variant="grey" icon={PenIcon} onClick={onEdit} />}
        </div>
      </div>
      <div className="h-4" />
      <div className="flex flex-col gap-2">
        <div className="flex items-center gap-4 justify-between">
          <div className="flex items-center gap-1">
            <ChildrenIcon width="20px" height="20px" color={tailwindColors.grey.lighter} />
            <Typography variant="label-medium" className="text-grey-lighter">
              {t(
                studentsCount !== 1
                  ? 'center.groups.students.assigned'
                  : 'center.groups.student.assigned',
                { total: studentsCount }
              )}
            </Typography>
          </div>
          {onViewStudents && (
            <ActionButton
              size="small"
              variant="grey"
              icon={OpenEyeIcon}
              onClick={onViewStudents}
            />
          )}
        </div>

        <div className="flex items-center gap-4 justify-between">
          <div className="flex items-center gap-1">
            <DocumentIcon width="20px" height="20px" color={tailwindColors.grey.lighter} />
            <Typography variant="label-medium" className="text-grey-lighter">
              {t(documentsCount !== 1 ? 'center.groups.documents' : 'center.groups.document', {
                total: documentsCount
              })}
            </Typography>
          </div>
          {onUploadDocument && (
            <ActionButton
              size="small"
              variant="grey"
              icon={UploadIcon}
              onClick={onUploadDocument}
            />
          )}
        </div>

        <div className="flex items-center gap-4 justify-between">
          <div className="flex items-center gap-1">
            <UserTwoIcon
              width="20px"
              height="20px"
              color={!hasTefl ? tailwindColors.grey.light.active : tailwindColors.grey.lighter}
            />
            <Typography
              variant="label-medium"
              className={cn({ 'text-grey-lighter': hasTefl, 'text-grey-light-active': !hasTefl })}
            >
              {group.tefl
                ? toFullName(group.tefl.firstName, group.tefl.lastName)
                : t('center.groups.no.tefl.assigned')}
            </Typography>
          </div>
          {onAssignTefl && (
            <ActionButton
              size="small"
              variant="grey"
              icon={hasTefl ? PenIcon : AddUserIcon}
              onClick={onAssignTefl}
            />
          )}
        </div>
      </div>
    </div>
  )
}

export default CenterGroupCard
